import { NetworkStatus, QueryResult, gql, useQuery } from '@apollo/client';
import * as React from 'react';

import { EAttribute_InputType } from '@/graphql-types/globalTypes';
import { useDebouncedValue } from '@/imports/ui/hooks/useDebouncedValue';

import { MemberDirectory_FilterOptions } from './graphql-types/MemberDirectory_FilterOptions';
import {
  MemberDirectory_Search,
  MemberDirectory_SearchVariables,
} from './graphql-types/MemberDirectory_Search';

import {
  MEMBER_DIRECTORY_FREE_TEXT_SEARCH_DEBOUNCE_MS,
  MEMBER_DIRECTORY_PAGE_SIZE,
  MEMBER_DIRECTORY_SEARCH_DEBOUNCE_MS,
} from '../constants';
import { MemberDirectoryFilterOptions } from '../types';
import {
  buildMemberDirectorySearchFilters,
  isFreeTextDirectoryFilter,
  useMemberDirectoryFilters,
} from './useMemberDirectoryFilters';

export const MEMBER_DIRECTORY_SEARCH = gql`
  query MemberDirectory_Search($input: MemberDirectorySearchInput!) {
    memberDirectorySearch(input: $input) {
      totalCount
      results {
        _id
        displayName
        profilePictureUrl
        integrationIsMember
        membershipData {
          startDateMs
          gracePeriodEndDateMs
          endDateMs
        }
        profile {
          preferredName
          prefix
          firstName
          lastName
          suffix
          nameTitle
          jobTitle
          company
          email
          primaryPhone
          addressLocality
          addressAdministrativeArea
          addressRegionCode
          addressLine0
          addressPostalCode
        }
        cardAttributes {
          attributeSchemaId
          name
          displayValues
        }
      }
    }
  }
`;

export const MEMBER_DIRECTORY_FILTER_OPTIONS = gql`
  query MemberDirectory_FilterOptions {
    memberDirectoryFilterOptions {
      attributeFilters {
        _id
        name
        inputType
        options {
          label
        }
      }
    }
  }
`;

export type UseMemberDirectorySearchResult = {
  isLoading: boolean;
  isInitialLoading: boolean;
  searchText: string;
  setSearchText: (value: string) => void;
  freeTextValues: Record<string, string>;
  setFreeTextValue: (attributeSchemaId: string, value: string) => void;
  page: number;
  setPage: (page: number) => void;
  limit: number;
  results: MemberDirectory_Search['memberDirectorySearch']['results'];
  totalCount: number;
  totalPages: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  selectedFilters: ReturnType<
    typeof useMemberDirectoryFilters
  >['selectedFilters'];
  unselectedFilters: ReturnType<
    typeof useMemberDirectoryFilters
  >['unselectedFilters'];
  selectFilter: ReturnType<typeof useMemberDirectoryFilters>['selectFilter'];
  unselectFilter: (params: { attributeSchemaId: string }) => void;
  isLoadingFilters: boolean;
  someFilterIsApplied: boolean;
  isSearchDebouncing: boolean;
  isFreeTextSearchDebouncing: boolean;
  /** True while a fetch was triggered by search/free-text debounce (not pagination). */
  isSearchQueryFetching: boolean;
  memberDirectorySearch: Pick<
    QueryResult<MemberDirectory_Search, MemberDirectory_SearchVariables>,
    'data' | 'networkStatus' | 'error' | 'loading'
  >;
  refetch: () => void;
};

export const useMemberDirectorySearch = (): UseMemberDirectorySearchResult => {
  const [searchText, setSearchText] = React.useState('');
  const [freeTextValues, setFreeTextValues] = React.useState<
    Record<string, string>
  >({});
  const [page, setPage] = React.useState(1);
  const [accumulatedResults, setAccumulatedResults] = React.useState<
    MemberDirectory_Search['memberDirectorySearch']['results']
  >([]);

  const debouncedSearchText = useDebouncedValue(
    searchText,
    MEMBER_DIRECTORY_SEARCH_DEBOUNCE_MS
  );
  const debouncedFreeTextValues = useDebouncedValue(
    freeTextValues,
    MEMBER_DIRECTORY_FREE_TEXT_SEARCH_DEBOUNCE_MS
  );

  const filterOptionsQuery = useQuery<MemberDirectory_FilterOptions>(
    MEMBER_DIRECTORY_FILTER_OPTIONS,
    {
      fetchPolicy: 'cache-first',
    }
  );

  const filterOptions: MemberDirectoryFilterOptions | undefined =
    filterOptionsQuery.data?.memberDirectoryFilterOptions
      ? {
          attributeFilters:
            filterOptionsQuery.data.memberDirectoryFilterOptions.attributeFilters.map(
              (attributeFilter) => ({
                _id: attributeFilter._id,
                name: attributeFilter.name,
                inputType: attributeFilter.inputType as EAttribute_InputType,
                options: attributeFilter.options.map((option) => ({
                  label: option.label,
                })),
              })
            ),
        }
      : undefined;

  const {
    selectedFilters,
    unselectedFilters,
    selectFilter,
    unselectFilter: unselectFilterBase,
    isLoadingFilters,
    someSelectFilterApplied,
  } = useMemberDirectoryFilters(filterOptions, filterOptionsQuery.loading);

  const setFreeTextValue = React.useCallback(
    (attributeSchemaId: string, value: string) => {
      setFreeTextValues((previous) => ({
        ...previous,
        [attributeSchemaId]: value,
      }));
    },
    []
  );

  const unselectFilter = React.useCallback(
    (params: { attributeSchemaId: string }) => {
      unselectFilterBase(params);
      setFreeTextValues((previous) => {
        if (!(params.attributeSchemaId in previous)) {
          return previous;
        }
        const next = { ...previous };
        delete next[params.attributeSchemaId];
        return next;
      });
    },
    [unselectFilterBase]
  );

  const searchFilterInput = React.useMemo(
    () =>
      buildMemberDirectorySearchFilters(
        selectedFilters,
        debouncedFreeTextValues
      ),
    [selectedFilters, debouncedFreeTextValues]
  );

  const someFreeTextFilterApplied = selectedFilters.some(
    (filter) =>
      isFreeTextDirectoryFilter(filter) &&
      Boolean(debouncedFreeTextValues[filter.attributeSchema._id]?.trim())
  );

  const someFilterIsApplied =
    someSelectFilterApplied || someFreeTextFilterApplied;

  const attributeFiltersKey = React.useMemo(
    () => JSON.stringify(searchFilterInput.attributeFilters ?? null),
    [searchFilterInput]
  );
  const stateSearchTextKey = searchFilterInput.stateSearchText ?? '';

  React.useEffect(() => {
    setPage(1);
    setAccumulatedResults([]);
  }, [debouncedSearchText, attributeFiltersKey, stateSearchTextKey]);

  const searchVariables = React.useMemo<MemberDirectory_SearchVariables>(
    () => ({
      input: {
        searchText: debouncedSearchText === '' ? null : debouncedSearchText,
        stateSearchText: searchFilterInput.stateSearchText,
        attributeFilters: searchFilterInput.attributeFilters,
        limit: MEMBER_DIRECTORY_PAGE_SIZE,
        offset: (page - 1) * MEMBER_DIRECTORY_PAGE_SIZE,
      },
    }),
    // attributeFiltersKey / stateSearchTextKey stabilize searchFilterInput fields
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see keys above
    [debouncedSearchText, page, attributeFiltersKey, stateSearchTextKey]
  );

  const { refetch, ...memberDirectorySearch } = useQuery<
    MemberDirectory_Search,
    MemberDirectory_SearchVariables
  >(MEMBER_DIRECTORY_SEARCH, {
    variables: searchVariables,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });

  const searchData =
    memberDirectorySearch.networkStatus === NetworkStatus.setVariables
      ? memberDirectorySearch.previousData?.memberDirectorySearch
      : memberDirectorySearch.data?.memberDirectorySearch;

  const results = React.useMemo(
    () => searchData?.results ?? [],
    [searchData?.results]
  );
  const totalCount = searchData?.totalCount ?? 0;
  const totalPages = Math.max(
    1,
    Math.ceil(totalCount / MEMBER_DIRECTORY_PAGE_SIZE)
  );

  React.useEffect(() => {
    if (memberDirectorySearch.networkStatus === NetworkStatus.setVariables) {
      return;
    }

    if (page === 1) {
      setAccumulatedResults(results);
      return;
    }

    setAccumulatedResults((previous) => {
      const existingIds = new Set(previous.map((member) => member._id));
      const newMembers = results.filter(
        (member) => !existingIds.has(member._id)
      );
      return [...previous, ...newMembers];
    });
  }, [memberDirectorySearch.networkStatus, page, results]);

  React.useEffect(() => {
    if (page > totalPages) {
      setPage(1);
    }
  }, [page, totalPages]);

  const isLoading =
    memberDirectorySearch.loading ||
    memberDirectorySearch.networkStatus === NetworkStatus.setVariables;

  const isInitialLoading =
    (filterOptionsQuery.loading && !filterOptions) ||
    (memberDirectorySearch.loading && results.length === 0);

  const isSearchDebouncing =
    searchText !== '' && searchText !== debouncedSearchText;

  const freeTextValuesKey = JSON.stringify(freeTextValues);
  const debouncedFreeTextValuesKey = JSON.stringify(debouncedFreeTextValues);
  const isFreeTextSearchDebouncing =
    freeTextValuesKey !== debouncedFreeTextValuesKey;

  const isSearchQueryFetching =
    memberDirectorySearch.networkStatus === NetworkStatus.setVariables &&
    (debouncedSearchText !== '' || someFreeTextFilterApplied);

  const hasMore = page < totalPages;

  const loadMore = React.useCallback(() => {
    if (page < totalPages && !isLoading) {
      setPage((currentPage) => currentPage + 1);
    }
  }, [page, totalPages, isLoading]);

  const isLoadingMore = isLoading && accumulatedResults.length > 0;

  return {
    isLoading,
    isSearchQueryFetching,
    isInitialLoading,
    searchText,
    setSearchText,
    freeTextValues,
    setFreeTextValue,
    page,
    setPage,
    limit: MEMBER_DIRECTORY_PAGE_SIZE,
    results: accumulatedResults,
    totalCount,
    totalPages,
    hasMore,
    isLoadingMore,
    loadMore,
    selectedFilters,
    unselectedFilters,
    selectFilter,
    unselectFilter,
    isLoadingFilters,
    someFilterIsApplied,
    isSearchDebouncing,
    isFreeTextSearchDebouncing,
    memberDirectorySearch,
    refetch: () => {
      refetch();
    },
  };
};
